import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { Badge, Chip, Track } from '../../../school_panel/components/ui';
import { initialsOf } from '../../../school_panel/components/studentRow';
import type { StaffSchoolTask } from '../../../school_panel/api/types';
import { taskChips } from '../taskChips';
import { taskDateLabel, taskProgress, type TaskStatus } from '../taskStatus';
import KindBadge from '../../Worksheets/components/KindBadge';

export function kindLabel(t: StaffSchoolTask): string {
  return t.kind === 'exam' ? 'Exam' : 'Notice';
}

/** Design `.fill` is slate; only a follow-up task gets the accent `.fill.lo`. */
export function trackTone(status: TaskStatus): 'slate' | 'accent' {
  return status === 'follow_up' ? 'accent' : 'slate';
}

export function TaskChips({ task }: { task: StaffSchoolTask }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      {taskChips(task).map((c) => (
        <Chip key={c} tone={/ late$/.test(c) ? 'warn' : / missing$/.test(c) ? 'down' : 'mute'}>{c}</Chip>
      ))}
    </span>
  );
}

export function TaskProgress({ task, status, wide = false }: { task: StaffSchoolTask; status: TaskStatus; wide?: boolean }) {
  const { pct } = taskProgress(task);
  return (
    <span className="flex items-center gap-2">
      <Track className="flex-1" value={pct ?? 0} tone={trackTone(status)} label={`${task.title} progress`} />
      <span className={`text-right font-mono text-xs text-ink ${wide ? 'w-[52px]' : ''}`}>{pct == null ? '—' : `${pct}%`}</span>
    </span>
  );
}

/** Exam progress, openable as the per-student tracker; notices show the plain bar. */
export function TrackerProgress({ task, status, wide = false, onOpenTracker }: { task: StaffSchoolTask; status: TaskStatus; wide?: boolean; onOpenTracker: (t: StaffSchoolTask) => void }) {
  if (task.kind !== 'exam') return <TaskProgress task={task} status={status} wide={wide} />;
  return (
    <span className="flex items-center gap-2">
      <button type="button" aria-label={`Who's done: ${task.title}`} onClick={() => onOpenTracker(task)}
        className="min-w-0 flex-1 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
        <TaskProgress task={task} status={status} wide={wide} />
      </button>
      <button type="button" onClick={() => onOpenTracker(task)}
        className="flex-none text-xs font-semibold text-slate hover:underline">Students</button>
    </span>
  );
}

/** Design `.avs`: a 26px initials circle with the name as a tooltip; `named` adds the visible name (list view). */
export function Owner({ name, named = false }: { name: string; named?: boolean }) {
  const circle = (
    <span aria-hidden="true" className="grid h-[26px] w-[26px] flex-none place-items-center rounded-full border-2 border-surface bg-mist text-[10px] font-semibold text-ink">{initialsOf(name)}</span>
  );
  if (!named) return <span title={name} className="inline-flex flex-none">{circle}<span className="sr-only">{name}</span></span>;
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      {circle}
      <span className="truncate text-[13px] text-ink">{name}</span>
    </span>
  );
}

export function TaskTitle({ task, onOpenNotice, className = '' }: { task: StaffSchoolTask; onOpenNotice: (t: StaffSchoolTask) => void; className?: string }) {
  const cls = `font-semibold text-ink hover:underline ${className}`;
  return task.kind === 'exam' && task.exam
    ? <Link to={`/exams/report/${task.exam.id}`} className={cls}>{task.title}</Link>
    : <button type="button" onClick={() => onOpenNotice(task)} className={`text-left ${cls}`}>{task.title}</button>;
}

interface Props { task: StaffSchoolTask; status: TaskStatus; onOpenNotice: (t: StaffSchoolTask) => void; onOpenTracker: (t: StaffSchoolTask) => void }

/** One task as a board card. */
export default function TaskRow({ task: t, status, onOpenNotice, onOpenTracker }: Props) {
  const late = status === 'follow_up';
  const date = taskDateLabel(t, status);
  return (
    <article className="flex flex-col gap-2.5 rounded-[16px] border border-line bg-surface p-3.5 transition hover:-translate-y-0.5 hover:shadow-card-hover">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-ink">{kindLabel(t)}</span>
        <span className="flex items-center gap-1.5">
          <KindBadge kind={t.exam?.kind} />
          {(t.to_mark ?? 0) > 0 && <Badge tone="warning">{t.to_mark} to mark</Badge>}
        </span>
      </div>
      <div className="min-w-0">
        <TaskTitle task={t} onOpenNotice={onOpenNotice} className="block break-words leading-snug" />
        <p className="mt-0.5 flex flex-wrap gap-x-1 text-xs text-muted">
          <span>{t.subject?.name ?? 'Notice'}</span>
          <span aria-hidden="true">·</span>
          <span>{t.audience_label}</span>
        </p>
      </div>
      <TrackerProgress task={t} status={status} onOpenTracker={onOpenTracker} />
      <TaskChips task={t} />
      <div className="flex items-center justify-between gap-2">
        {date
          ? <span className={`inline-flex items-center gap-[5px] font-mono text-xs font-semibold ${late ? 'text-danger-ink' : 'text-muted-2'}`}><CalendarDays size={14} strokeWidth={1.8} aria-hidden="true" />{date}</span>
          : <span />}
        <Owner name={t.created_by} />
      </div>
    </article>
  );
}
